import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
// import { Button } from "@/components/ui/button";
import { Check, AlertTriangle, TrendingUp, TrendingDown, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { ClinicalDataLanguageNotice } from "@/components/ClinicalDataLanguageNotice";
import { LOINC_TO_PANEL, PANEL_LABEL_KEYS } from "./records/lab-panels";

export interface LabResult {
  id: string;
  name: string;
  value: string | number;
  unit: string;
  referenceRange: string;
  status: string;
  date: string;
  abnormal: boolean;
  loincCode?: string;
}

interface LabResultsTableProps {
  labResults: LabResult[];
  onAskQuestion?: (question: string) => void;
}

// This table's own panel display order (kept distinct from lab-panels.ts's PANEL_ORDER,
// which the new grouped LabPanels view uses — this preserves the legacy table's original order).
const TABLE_PANEL_ORDER = ['CBC', 'Chemistry', 'Liver', 'Coagulation', 'Inflammatory', 'Blood Gas', 'Viral', 'Other'];

// Panel header colors (avoiding green/orange/red used by status badges)
const PANEL_COLORS: Record<string, string> = {
  'CBC': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Chemistry': 'bg-sky-100 text-sky-800 border-sky-200',
  'Liver': 'bg-amber-100 text-amber-800 border-amber-200',
  'Coagulation': 'bg-purple-100 text-purple-800 border-purple-200',
  'Inflammatory': 'bg-pink-100 text-pink-800 border-pink-200',
  'Blood Gas': 'bg-teal-100 text-teal-800 border-teal-200',
  'Viral': 'bg-green-100 text-green-800 border-green-200',
  'Other': 'bg-slate-100 text-slate-700 border-slate-200',
};

function getLabPanel(result: LabResult): string {
  // First try LOINC code lookup
  if (result.loincCode && LOINC_TO_PANEL[result.loincCode]) {
    return LOINC_TO_PANEL[result.loincCode];
  }
  // Fallback: try to match by test name
  const nameLower = result.name.toLowerCase();
  if (nameLower.includes('wbc') || nameLower.includes('rbc') || nameLower.includes('hemoglobin') ||
      nameLower.includes('hematocrit') || nameLower.includes('platelet') || nameLower.includes('neutrophil') ||
      nameLower.includes('lymphocyte') || nameLower.includes('mcv') || nameLower.includes('mch')) return 'CBC';
  if (nameLower.includes('sodium') || nameLower.includes('potassium') || nameLower.includes('chloride') ||
      nameLower.includes('glucose') || nameLower.includes('bun') || nameLower.includes('creatinine') ||
      nameLower.includes('co2') || nameLower.includes('bicarbonate')) return 'Chemistry';
  if (nameLower.includes('ast') || nameLower.includes('alt') || nameLower.includes('bilirubin') ||
      nameLower.includes('albumin') || nameLower.includes('alkaline') || nameLower.includes('liver')) return 'Liver';
  if (nameLower.includes('pt') || nameLower.includes('ptt') || nameLower.includes('inr') ||
      nameLower.includes('fibrinogen') || nameLower.includes('coag')) return 'Coagulation';
  if (nameLower.includes('crp') || nameLower.includes('procalcitonin') || nameLower.includes('lactate') ||
      nameLower.includes('sed rate') || nameLower.includes('esr')) return 'Inflammatory';
  if (nameLower.includes('ph') || nameLower.includes('pco2') || nameLower.includes('po2') ||
      nameLower.includes('hco3') || nameLower.includes('blood gas')) return 'Blood Gas';
  return 'Other';
}

export default function LabResultsTable({ labResults, onAskQuestion }: LabResultsTableProps) {
  const { t } = useTranslation();
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      normal: { icon: Check, className: "bg-success text-white", label: t('records.statusNormal') },
      low: { icon: TrendingDown, className: "bg-accent text-white", label: t('records.statusLow') },
      high: { icon: TrendingUp, className: "bg-accent text-white", label: t('records.statusHigh') },
      critical: { icon: AlertTriangle, className: "bg-error text-white", label: t('records.statusCritical') },
    };

    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    const config = statusConfig[normalizedStatus as keyof typeof statusConfig] || statusConfig.normal;
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const handleAskAboutResult = (testName: string, result: string | number, status: string) => {
    if (onAskQuestion) {
      const question = t('explain.labResult', { testName, result, status });
      onAskQuestion(question);
    }
  };

  if (!labResults || labResults.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <p>{t('records.noLabResultsAvailable')}</p>
      </div>
    );
  }

  // Remove duplicates (same test name, date, and time) and sort by date (most recent first)
  const deduplicatedResults = labResults.reduce((acc, result) => {
    // Create a unique key based on test name + date/time + value
    const key = `${result.name}-${result.date}-${result.value}`;
    if (!acc.seen.has(key)) {
      acc.seen.add(key);
      acc.results.push(result);
    }
    return acc;
  }, { seen: new Set<string>(), results: [] as LabResult[] }).results;

  const sortedResults = deduplicatedResults.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  // Group by panel
  const groupedByPanel = sortedResults.reduce((acc, result) => {
    const panel = getLabPanel(result);
    if (!acc[panel]) acc[panel] = [];
    acc[panel].push(result);
    return acc;
  }, {} as Record<string, LabResult[]>);

  return (
    <div className="overflow-x-auto">
      <ClinicalDataLanguageNotice />
      {TABLE_PANEL_ORDER.map(panel => {
        const results = groupedByPanel[panel];
        if (!results || results.length === 0) return null;
        return (
          <div key={panel} className="mb-3">
            <div className={`text-xs font-semibold px-2 py-1.5 sticky top-0 z-10 border-b ${PANEL_COLORS[panel] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
              {PANEL_LABEL_KEYS[panel] ? t(PANEL_LABEL_KEYS[panel]) : panel}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left text-xs py-1.5">{t('records.dateColumn')}</TableHead>
                  <TableHead className="text-left text-xs py-1.5">{t('records.testColumn')}</TableHead>
                  <TableHead className="text-left text-xs py-1.5">{t('records.resultColumn')}</TableHead>
                  <TableHead className="text-left text-xs py-1.5">{t('records.referenceColumn')}</TableHead>
                  <TableHead className="text-left text-xs py-1.5">{t('records.statusColumn')}</TableHead>
                  <TableHead className="text-left text-xs py-1.5"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((result) => (
                  <TableRow
                    key={result.id}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <TableCell className="text-xs py-1.5 text-text-secondary whitespace-nowrap">
                      <div>{format(new Date(result.date), "MMM d, yyyy")}</div>
                      <div className="text-[10px] text-gray-400">{format(new Date(result.date), "h:mm a")}</div>
                    </TableCell>
                    <TableCell className="font-medium text-xs py-1.5">
                      {result.name}
                    </TableCell>
                    <TableCell className="text-xs py-1.5">
                      <span className="font-medium">
                        {result.value} {result.unit && <span className="text-text-secondary text-xs">{result.unit}</span>}
                      </span>
                    </TableCell>
                    <TableCell className="text-text-secondary text-xs py-1.5">
                      {result.referenceRange || t('records.notAvailable')}
                    </TableCell>
                    <TableCell className="py-1.5">
                      {getStatusBadge(result.status)}
                    </TableCell>
                    <TableCell className="py-1.5">
                      <button
                        onClick={() => handleAskAboutResult(result.name, result.value, result.status)}
                        className="bg-white border border-gray-300 hover:border-primary text-primary hover:bg-primary hover:text-white px-2.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-all shadow-sm whitespace-nowrap"
                        data-testid={`button-explain-test-${result.id}`}
                        aria-label={t('explain.labResultAriaLabel', { testName: result.name })}
                      >
                        <MessageSquare className="h-3 w-3" />
                        {t('explain.buttonLabel')}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        );
      })}
    </div>
  );
}
